
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSavedTutors } from '@/hooks/useSavedTutors';
import { isAdmin } from '@/utils/roleValidation';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import { pb } from '@/lib/pocketbase';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Menu, LogOut, Heart, LayoutDashboard, ShoppingCart, BarChart2, FileText, DollarSign, Settings, Megaphone, Loader2, Star, ChevronDown, Code, Music, Dumbbell, MapPin, MessageSquare, Users, Search, Calendar, GraduationCap, Layers } from 'lucide-react';
import NotificationBadge from '@/components/NotificationBadge';
import AdminNotificationBadge from '@/components/AdminNotificationBadge';
import MessagesDropdown from '@/components/MessagesDropdown';
import CurrencySelector from '@/components/CurrencySelector';
import SearchModal from '@/components/SearchModal';

interface NavItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const Navbar = () => {
  const { user, logout, isLoggingOut } = useAuth();
  const { savedTutors } = useSavedTutors();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubjectsOpen, setIsSubjectsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState('');

  // Determine if current page should have full height navbar
  const isFullHeightPage = 
    location.pathname === '/' ||
    location.pathname === '/subjects/english' ||
    location.pathname === '/subjects/programming' ||
    location.pathname === '/subjects/music' ||
    location.pathname === '/subjects/fitness' ||
    location.pathname === '/subjects/travel' ||
    location.pathname === '/inspire-posts' ||
    location.pathname === '/explore-lessons' ||
    location.pathname === '/auth' ||
    location.pathname === '/contact' ||
    location.pathname === '/how-it-works' ||
    location.pathname === '/subjects' ||
    location.pathname === '/announcements' ||
    location.pathname === '/shop' ||
    location.pathname === '/tutors' ||
    location.pathname === '/featured' ||
    location.pathname === '/articles' ||
    location.pathname === '/courses' ||
    location.pathname.startsWith('/courses/') ||
    location.pathname.startsWith('/tutor/');

  const subjects = [
    { label: 'English', href: '/subjects/english', icon: BookOpen },
    { label: 'Programming', href: '/subjects/programming', icon: Code },
    { label: 'Music', href: '/subjects/music', icon: Music },
    { label: 'Fitness', href: '/subjects/fitness', icon: Dumbbell },
    { label: 'Travel', href: '/subjects/travel', icon: MapPin },
  ];

  const navItems: NavItem[] = [
    { label: 'Tutors', href: '/tutors' },
    { label: 'Featured', href: '/featured', icon: Layers },
    { label: 'Shop', href: '/shop', icon: ShoppingCart },
    { label: 'Game', href: '/language-adventure' },
    { label: 'Announcements', href: '/announcements', icon: Megaphone },
    { label: 'Platform', href: '/how-it-works' },
    { label: 'Subjects', href: '/subjects' },
    { label: 'Contact', href: '/contact' },
  ];

  // Use PocketBase user
  const currentUser = user;
  const userName = user?.name || 'User';
  
  // Fetch tutor/student photo from respective collections
  useEffect(() => {
    const fetchUserPhoto = async () => {
      if (!user?.id) {
        console.log('Navbar: No user id, clearing photo');
        setUserPhotoUrl('');
        return;
      }

      console.log('Navbar: Fetching photo for user:', user.id, 'role:', user.role);

      try {
        if (user?.role === 'tutor') {
          // Fetch from tutors collection
          console.log('Navbar: Fetching tutor profile from tutors collection');
          const tutors = await pb.collection('tutors').getList(1, 1, {
            filter: `user = "${user.id}"`
          });
          
          console.log('Navbar: Tutors response:', tutors);
          
          if (tutors.items.length > 0) {
            const tutorProfile = tutors.items[0];
            console.log('Navbar: Tutor profile:', tutorProfile);
            console.log('Navbar: profilePicture field:', tutorProfile.profilePicture);
            console.log('Navbar: profilePictureUrl field:', tutorProfile.profilePictureUrl);
            
            const photoUrl = getProfilePictureUrl(tutorProfile);
            console.log('Navbar: Generated photo URL:', photoUrl);
            setUserPhotoUrl(photoUrl);
          } else {
            console.log('Navbar: No tutor profile found');
            setUserPhotoUrl('');
          }
        } else if (user?.role === 'student') {
          // Fetch from student_profiles collection - use 'user' field not 'userId'
          console.log('Navbar: Fetching student profile from student_profiles collection');
          const studentProfiles = await pb.collection('student_profiles').getList(1, 1, {
            filter: `user = "${user.id}"`
          });
          
          console.log('Navbar: Student profiles response:', studentProfiles);
          
          if (studentProfiles.items.length > 0) {
            const studentProfile = studentProfiles.items[0];
            console.log('Navbar: Student profile:', studentProfile);
            
            const photoUrl = getProfilePictureUrl(studentProfile);
            console.log('Navbar: Generated photo URL:', photoUrl);
            setUserPhotoUrl(photoUrl);
          } else {
            console.log('Navbar: No student profile found');
            setUserPhotoUrl('');
          }
        } else {
          // For other roles, use avatar field
          console.log('Navbar: Other role user, using avatar:', user?.avatar);
          const photoUrl = user?.avatar ? getProfilePictureUrl(user) : '';
          console.log('Navbar: Generated photo URL:', photoUrl);
          setUserPhotoUrl(photoUrl);
        }
      } catch (error) {
        console.error('Navbar: Error fetching user photo:', error);
        setUserPhotoUrl('');
      }
    };

    fetchUserPhoto();
  }, [user?.id, user?.role, user?.avatar]);

  // Put dashboard at the top with special styling if user is admin
  const allNavItems: NavItem[] = (currentUser && isAdmin(user))
    ? [{ label: 'Dashboard', href: '/admin', icon: LayoutDashboard }, ...navItems]
    : navItems;

  const publicNavItems: NavItem[] = navItems; // Show all nav items including announcements for public

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // If already on homepage, scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to homepage
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    // Close mobile sheet if open
    setIsOpen(false);
    // Navigate to home immediately
    navigate('/');
  };

  const handleStartTeaching = () => {
    navigate('/auth', { state: { defaultTab: 'register' } });
    setIsOpen(false);
  };

  return (
    <nav className="bg-white shadow-sm border-b fixed top-0 left-0 right-0 z-50 w-full">
      <div className="w-full px-4 py-0 lg:py-1 xl:py-2">
        <div className={`flex items-center ${
          isFullHeightPage 
            ? 'h-12 lg:h-14 xl:h-16' 
            : 'h-10 lg:h-10 xl:h-12'
        }`}>
          {/* Left: Logo */}
          <div className="flex items-center flex-shrink-0">
          <div 
            className="flex flex-col cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleLogoClick}
          >
            <div className="flex items-center space-x-0.5 md:space-x-1">
              <div className="h-4 w-4 md:h-8 md:w-8 lg:h-9 lg:w-9 xl:h-10 xl:w-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <BookOpen className="h-2.5 w-2.5 md:h-5 md:w-5 lg:h-5.5 lg:w-5.5 xl:h-6 xl:w-6 text-white" />
              </div>
              <span className="text-[11px] md:text-lg lg:text-xl xl:text-xl font-bold">
                <span className="text-gray-900">Tutor</span>
                <span className="text-brand-violet">Nesta</span>
              </span>
            </div>
          </div>
          </div>

          {/* Center: Mobile Navigation - Show only on small screens */}
          <div className="lg:hidden flex items-center gap-1 mx-auto sm:gap-2">
            {/* Text Navigation Items */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/inspire-posts')}
              className="h-7 px-1.5 text-gray-600 hover:bg-gray-50 text-[10px] font-medium sm:h-9 sm:px-3 sm:text-base"
            >
              Posts
            </Button>

            {/* Content Dropdown - Articles, Lessons, Courses */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-1.5 text-gray-600 hover:bg-gray-50 text-[10px] font-medium sm:h-9 sm:px-3 sm:text-base focus:outline-none focus:ring-0 focus-visible:ring-0 border-none"
                >
                  Content
                  <ChevronDown className="h-3 w-3 ml-0.5 sm:h-4 sm:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="bg-white border shadow-lg z-[9999] w-48">
                <DropdownMenuItem 
                  onClick={() => navigate('/articles')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span>Articles</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/explore-lessons')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <BookOpen className="h-4 w-4 text-gray-500" />
                  <span>Lessons</span>
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/courses')}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                  >
                    <GraduationCap className="h-4 w-4 text-gray-500" />
                    <span>Courses</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => navigate('/featured')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <Layers className="h-4 w-4 text-gray-500" />
                  <span>أقسام أخرى</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu open={isSubjectsOpen} onOpenChange={setIsSubjectsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-1.5 text-gray-600 hover:bg-gray-50 text-[11px] font-medium focus:outline-none focus:ring-0 focus-visible:ring-0 border-none sm:h-8 sm:px-2.5 sm:text-sm"
                  title="Subjects"
                >
                  <Layers className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="bg-white border shadow-lg z-[9999] w-48">
                {subjects.map((subject) => (
                  <DropdownMenuItem 
                    key={subject.label}
                    onClick={() => {
                      navigate(subject.href);
                      setIsSubjectsOpen(false);
                    }}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                  >
                    <subject.icon className="h-4 w-4 text-gray-500" />
                    <span>{subject.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Utility Icons Group - Icons Only */}
            <div className="flex items-center gap-0 ml-1 pl-1 border-l border-gray-200 sm:gap-1 sm:ml-1 sm:pl-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSearchOpen(true)}
                className="h-7 w-7 p-0 text-gray-600 hover:bg-gray-50"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </Button>

              {currentUser && (
                <>
                  <MessagesDropdown buttonClassName="h-7 w-7 p-0 hover:bg-gray-100" />
                  <div className="h-7 w-7 flex items-center justify-center">
                    {isAdmin(user) ? <AdminNotificationBadge /> : <NotificationBadge />}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: Mobile Menu Button */}
          <div className="lg:hidden flex items-center flex-shrink-0 ml-auto">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Menu className="h-3.5 w-3.5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side={window.location.pathname === '/success-stories' ? "bottom" : "right"}
                className={`bg-white z-[60] max-h-screen overflow-y-auto p-4 ${
                  window.location.pathname === '/success-stories' 
                    ? 'w-full h-[80vh] rounded-t-lg' 
                    : 'w-80'
                }`}
              >
                <SheetHeader className="pb-2">
                  <SheetTitle className="text-base">Navigation Menu</SheetTitle>
                  <SheetDescription className="text-sm">
                    Access all TutorNesta features and your account settings
                  </SheetDescription>
                </SheetHeader>
                <div className="flex flex-col space-y-3 mt-3 pb-3">
                  {/* Mobile Currency Selector - Temporarily hidden */}
                  {/* <div className="border-b pb-2">
                    <h3 className="text-xs font-semibold text-gray-500 px-1 mb-2">Currency</h3>
                    <CurrencySelector className="w-full" />
                  </div> */}

                  {/* Mobile Navigation */}
                  <div className="flex flex-col space-y-1">
                    {/* Mobile Profile - Only show when logged in */}
                    {currentUser && (
                      <button
                        onClick={() => handleNavigation('/profile')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <Users className="h-4 w-4" />
                        <span>Profile</span>
                      </button>
                    )}

                    {/* My Courses - Only show for test user */}
                    {currentUser && currentUser.email === 'makarsameh87@gmail.com' && (
                      <button
                        onClick={() => handleNavigation('/my-courses')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <GraduationCap className="h-4 w-4" />
                        <span>My Courses</span>
                      </button>
                    )}
                    
                    {/* Dashboard link (Admin only) */}  
                    {currentUser && isAdmin(user) && (
                      <button
                        onClick={() => handleNavigation('/admin')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Dashboard</span>
                      </button>
                    )}
                    
                    {/* Mobile Posts Button */}
                    <button
                      onClick={() => handleNavigation('/inspire-posts')}
                      className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                    >
                      <Star className="h-4 w-4" />
                      <span>Posts</span>
                    </button>

                    {/* Mobile Articles Button */}
                    <button
                      onClick={() => handleNavigation('/articles')}
                      className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Articles</span>
                    </button>

                    {/* Mobile Lessons Button */}
                    <button
                      onClick={() => handleNavigation('/explore-lessons')}
                      className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>Lessons</span>
                    </button>

                    {/* Mobile Courses Button - Admin only for testing */}
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => handleNavigation('/courses')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <GraduationCap className="h-4 w-4" />
                        <span>Courses</span>
                      </button>
                    )}
                    
                    {/* Mobile Bookings Button */}
                    {currentUser && (
                      <button
                        onClick={() => handleNavigation('/lessons')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <Calendar className="h-4 w-4" />
                        <span>Bookings</span>
                      </button>
                    )}
                    
                    {/* Mobile Messages - Only show when logged in */}
                    {currentUser && (
                      <button
                        onClick={() => handleNavigation('/messages')}
                        className="text-left text-brand-violet hover:text-brand-violet/80 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        <MessageSquare className="h-4 w-4" />
                        <span>Messages</span>
                      </button>
                    )}
                    
                    {/* Rest of navigation items */}
                    {publicNavItems.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => handleNavigation(item.href)}
                        className="text-left text-gray-600 hover:text-blue-600 transition-colors py-1.5 flex items-center space-x-2 text-sm"
                      >
                        {item.icon && <item.icon className="h-4 w-4" />}
                        <span>{item.label}</span>
                      </button>
                    ))}
                    
                    {/* Mobile Saved Tutors - Only show when logged in */}
                    {currentUser && (
                      <button
                        onClick={() => handleNavigation('/saved-tutors')}
                        className="text-left text-gray-600 hover:text-red-600 transition-colors py-1.5 flex items-center text-sm"
                      >
                        <Heart className="h-4 w-4 mr-2" />
                        Saved Tutors
                        {savedTutors.length > 0 && (
                          <Badge className="ml-2 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                            {savedTutors.length}
                          </Badge>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Mobile Admin Tools Section */}
                  {isAdmin(user) && (
                    <div className="border-t pt-2">
                      <h3 className="text-xs font-semibold text-gray-500 px-1 mb-1">Admin Tools</h3>
                      <div className="flex flex-col space-y-1">
                         <button onClick={() => handleNavigation('/admin/users')} className="text-left text-gray-600 hover:text-blue-600 transition-colors py-1.5 flex items-center space-x-2 text-sm">
                           <Users className="h-4 w-4" />
                           <span>User Management</span>
                         </button>
                         <button onClick={() => handleNavigation('/admin/analytics')} className="text-left text-gray-600 hover:text-blue-600 transition-colors py-1.5 flex items-center space-x-2 text-sm">
                           <BarChart2 className="h-4 w-4" />
                           <span>Analytics</span>
                         </button>
                         <button onClick={() => handleNavigation('/admin/content')} className="text-left text-gray-600 hover:text-blue-600 transition-colors py-1.5 flex items-center space-x-2 text-sm">
                           <FileText className="h-4 w-4" />
                           <span>Content</span>
                         </button>
                         <button onClick={() => handleNavigation('/admin/financials')} className="text-left text-gray-600 hover:text-blue-600 transition-colors py-1.5 flex items-center space-x-2 text-sm">
                           <DollarSign className="h-4 w-4" />
                           <span>Financials</span>
                         </button>
                      </div>
                    </div>
                  )}

                  {/* Mobile Auth Section */}
                  <div className="border-t pt-3">
                    {currentUser ? (
                      <div className="flex flex-col space-y-2">
                        <button 
                          onClick={() => {
                            navigate('/profile');
                            setIsOpen(false);
                          }}
                          className="flex items-center space-x-3 hover:bg-blue-50 p-2 rounded-lg transition-colors cursor-pointer group"
                        >
                          <Avatar className="h-9 w-9 ring-2 ring-transparent group-hover:ring-blue-500 transition-all">
                            <AvatarImage src={userPhotoUrl} />
                            <AvatarFallback className="text-xs">
                              {userName?.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium group-hover:text-blue-600 transition-colors">{userName}</span>
                        </button>
                        
                        <Button
                          variant="ghost"
                          onClick={handleLogout}
                          disabled={isLoggingOut}
                          className="w-full h-8 text-sm"
                        >
                          {isLoggingOut ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Signing out...
                            </>
                          ) : (
                            <>
                              <LogOut className="h-4 w-4 mr-2" />
                              Logout
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col space-y-2">
                        <Button
                          variant="outline"
                          onClick={() => handleNavigation('/auth')}
                          className="w-full h-8 text-sm"
                        >
                          Sign In
                        </Button>
                        <Button
                          onClick={handleStartTeaching}
                          className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-sm"
                        >
                          Become a Tutor
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Center: Desktop Navigation - Show on large screens and up */}
          <div className="hidden lg:flex items-center gap-3 flex-1 justify-center">
            {/* Dashboard link (Admin only) */}
            {currentUser && isAdmin(user) && (
              <button
                onClick={() => handleNavigation('/admin')}
                className="text-blue-600 hover:text-blue-700 transition-colors text-sm px-2 py-1 font-medium"
              >
                <span>Dashboard</span>
              </button>
            )}
            
            {/* Tutors link - keep separate */}
            <button
              onClick={() => handleNavigation('/tutors')}
              className="text-gray-600 hover:text-blue-600 transition-colors text-sm px-2 py-1"
            >
              <span>Tutors</span>
            </button>

            {/* Featured Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/featured')}
              className="h-8 px-3 text-gray-600 hover:bg-gray-50 text-sm font-semibold"
            >
              Featured
            </Button>

            {/* Search Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSearchOpen(true)}
              className="h-8 w-8 p-0 text-gray-600 hover:bg-gray-50"
            >
              <Search className="h-4 w-4" />
            </Button>

            {/* Posts Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/inspire-posts')}
              className="h-8 px-3 text-gray-600 hover:bg-gray-50 text-sm font-semibold"
            >
              Posts
            </Button>

            {/* Content Dropdown - Articles, Lessons, Courses */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-gray-600 hover:bg-gray-50 text-sm font-semibold focus:outline-none focus:ring-0 focus-visible:ring-0 border-none"
                >
                  Content
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="bg-white border shadow-lg z-[9999] w-48">
                <DropdownMenuItem 
                  onClick={() => navigate('/articles')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span>Articles</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/explore-lessons')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <BookOpen className="h-4 w-4 text-gray-500" />
                  <span>Lessons</span>
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/courses')}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                  >
                    <GraduationCap className="h-4 w-4 text-gray-500" />
                    <span>Courses</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => navigate('/featured')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <Layers className="h-4 w-4 text-gray-500" />
                  <span>أقسام أخرى</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Bookings Button - Only show when logged in */}
            {currentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/lessons')}
                className="h-8 px-3 text-gray-600 hover:bg-gray-50 text-sm font-semibold"
              >
                Bookings
              </Button>
            )}

            {/* Subjects Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 hover:text-blue-600 transition-colors text-sm px-2 py-1 h-auto font-semibold"
                  title="Subjects"
                >
                  <Layers className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="bg-white border shadow-lg z-[9999] w-48">
                {subjects.map((subject) => (
                  <DropdownMenuItem 
                    key={subject.label}
                    onClick={() => handleNavigation(subject.href)}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                  >
                    <subject.icon className="h-4 w-4 text-gray-500" />
                    <span>{subject.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile Button - Only show when logged in */}
            {currentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/profile')}
                className="h-8 px-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 text-sm font-medium"
              >
                Profile
              </Button>
            )}

            {/* Currency Selector - Temporarily hidden */}
            {/* <CurrencySelector /> */}

            {/* Grouped dropdown for other items */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 hover:text-blue-600 transition-colors text-sm px-2 py-1 h-auto font-normal"
                >
                  More
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border shadow-lg z-[9999] w-48">
                <DropdownMenuItem 
                  onClick={() => handleNavigation('/shop')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <ShoppingCart className="h-4 w-4 text-gray-500" />
                  <span>Shop</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleNavigation('/language-adventure')}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <span>Game</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleNavigation('/announcements')}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50"
                >
                  <Megaphone className="h-4 w-4 text-gray-500" />
                  <span>Announcements</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleNavigation('/how-it-works')}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <span>Platform</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleNavigation('/contact')}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <span>Contact</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right: Action Buttons - Show on large screens and up */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            {currentUser && (
              <>
                <MessagesDropdown />
                {isAdmin(user) ? <AdminNotificationBadge /> : <NotificationBadge />}
              </>
            )}

            {/* Saved Tutors Button - Only show when logged in */}
            {currentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/saved-tutors')}
                className="relative text-gray-600 hover:text-red-600 h-8 w-8 p-0"
              >
                <Heart className="h-4 w-4 lg:h-3.5 lg:w-3.5 xl:h-4 xl:w-4" />
                {savedTutors.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                    {savedTutors.length}
                  </Badge>
                )}
              </Button>
            )}

            {/* Admin Tools Dropdown - Only for Admins */}
            {isAdmin(user) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1 h-8 lg:h-7 xl:h-8 px-2 lg:px-1 xl:px-2 text-sm lg:text-xs xl:text-sm">
                    <Settings className="h-3 w-3 lg:h-2.5 lg:w-2.5 xl:h-3 xl:w-3" />
                    <span className="lg:hidden xl:inline">Admin Tools</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border shadow-lg z-50">
                  <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                    <Users className="mr-2 h-4 w-4" />
                    <span>User Management</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/analytics')}>
                    <BarChart2 className="mr-2 h-4 w-4" />
                    <span>Analytics</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/content')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Content</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/financials')}>
                    <DollarSign className="mr-2 h-4 w-4" />
                    <span>Financials</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {currentUser ? (
              <div className="flex items-center space-x-2">
                {/* My Courses Button - Only for test user */}
                {currentUser.email === 'makarsameh87@gmail.com' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/my-courses')}
                    className="text-gray-600 hover:text-blue-600 h-8 px-2"
                    title="My Courses"
                  >
                    <GraduationCap className="h-4 w-4 lg:h-3.5 lg:w-3.5 xl:h-4 xl:w-4" />
                  </Button>
                )}
                <button 
                  onClick={() => navigate('/profile')}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer group"
                >
                  <Avatar className="h-8 w-8 lg:h-7 lg:w-7 ring-2 ring-transparent group-hover:ring-blue-500 transition-all">
                    <AvatarImage src={userPhotoUrl} />
                    <AvatarFallback className="text-xs">
                      {userName?.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm lg:text-xs font-medium hidden lg:block group-hover:text-blue-600 transition-colors">{userName}</span>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="h-8 w-8 p-0"
                >
                  {isLoggingOut ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/auth')}
                  className="text-gray-700 border-gray-300 hover:bg-gray-50 h-8 px-3 text-sm"
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  onClick={handleStartTeaching}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-sm"
                >
                  <span className="hidden xl:inline">Become a </span>Tutor
                </Button>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </nav>
  );
};

export default Navbar;
